import React, { useState, useMemo, useRef } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Grid,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { ISO_COUNTRY_CODES } from '@/constants/countries'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate()
  const { register } = useAuth()
  const { t } = useTranslation()

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    city: '',
    country: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<any>(null)

  // Country options with ISO code and translated label
  const countryOptions = useMemo(
    () =>
      ISO_COUNTRY_CODES.map((code) => ({
        code,
        label: t(`projects.countries.${code}`),
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [t]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('') // Clear error when user types
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Basic validation
    if (!formData.email || !formData.username || !formData.firstName || !formData.lastName) {
      setError(t('auth.register.requiredFields'))
      setLoading(false)
      return
    }

    // Privacy policy validation
    if (!acceptedPrivacy) {
      setError(t('auth.register.acceptPrivacy'))
      setLoading(false)
      return
    }

    // Turnstile validation
    if (!turnstileToken) {
      setError(t('auth.register.completeCaptcha'))
      setLoading(false)
      return
    }

    // Username validation
    const usernameRegex = /^[a-zA-Z0-9._]{3,20}$/
    if (!usernameRegex.test(formData.username)) {
      setError(t('auth.register.invalidUsername'))
      setLoading(false)
      return
    }

    try {
      await register({
        ...formData,
        turnstileToken: turnstileToken || undefined,
        acceptedPrivacy,
      })
      setSuccess(true)
      // Redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login')
      }, 5000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.register.error')
      // Map backend error messages to translation keys
      const errorMap: Record<string, string> = {
        'Username already taken': t('auth.register.errors.usernameTaken'),
        'Email already registered': t('auth.register.errors.emailTaken'),
        'This username is not available': t('auth.register.errors.usernameReserved'),
        'Username must be 3-20 characters long and contain only letters, numbers, dots, and underscores': t('auth.register.invalidUsername'),
        'You must accept the privacy policy to register': t('auth.register.acceptPrivacy'),
        'Security verification is required': t('auth.register.completeCaptcha'),
        'Security verification failed. Please try again.': t('auth.register.errors.captchaFailed'),
      }
      setError(errorMap[errorMessage] || errorMessage)
      // Reset Turnstile on error
      setTurnstileToken(null)
      turnstileRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
            <Typography component="h1" variant="h4" gutterBottom color="success.main">
              {t('auth.register.success.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.register.success.message')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.register.success.redirect')}
            </Typography>
          </Paper>
        </Box>
      </Container>
    )
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link component={RouterLink} to="/" underline="hover" sx={{ fontWeight: 700, fontSize: '1.2rem', color: 'text.primary', mb: 3, alignSelf: 'flex-start' }}>
          ← Orelytics
        </Link>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('auth.register.title')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.register.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label={t('auth.fields.username')}
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={loading}
                  helperText={t('auth.register.usernameHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="firstName"
                  label={t('auth.fields.firstName')}
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label={t('auth.fields.lastName')}
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label={t('auth.fields.email')}
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="city"
                  label={t('auth.fields.city')}
                  name="city"
                  autoComplete="address-level2"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="country"
                  options={countryOptions}
                  getOptionLabel={(option) => option.label}
                  value={countryOptions.find((c) => c.code === formData.country) || null}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      country: newValue?.code || '',
                    })
                    setError('')
                  }}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('auth.fields.country')}
                      autoComplete="country-name"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedPrivacy}
                      onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('auth.register.privacyLabel')}{' '}
                      <Link href="/privacy" target="_blank">
                        {t('auth.register.privacyLink')}
                      </Link>
                      {' '}{t('auth.register.andThe')}{' '}
                      <Link href="/terms" target="_blank">
                        {t('auth.register.termsLink')}
                      </Link>
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setTurnstileToken(null)}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </Box>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !acceptedPrivacy || !turnstileToken}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.register.submit')}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {t('auth.register.hasAccount')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}